import type { SignupFormProps } from './signup-form';
import { memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { usePostHog } from 'posthog-js/react';
import {
  autoSignIn,
  confirmSignIn,
  signIn,
  signOut,
  signUp,
} from 'aws-amplify/auth';
import {
  Form,
  Button,
  TextFieldControlled as Input,
  ToggleFieldControlled as Toggle,
  SelectFieldMinimal,
  FieldWrap,
  Paragraph,
} from '@leafwell/components';
import { useWordPressContext } from 'contexts/wordpress-provider';
import { clientId } from 'lib/tracking/gtag';
import { setStateCookie } from 'lib/cookies/state-cookie';
import { useEventsTracker } from 'contexts/eventsTracker';
import { useBrazeContext } from 'contexts/braze-context';
import { StatesListItem, useStatesList } from 'hooks/useStatesList';
import { useMedicalCardSignup } from 'hooks/useMedicalCardSignup';
import { useMedicalCardParams } from 'hooks/useMedicalCardParams';
import { getTemporaryUtmReferrerQuery } from './marketing-form';
import { configureAmplify } from 'lib/amplify';
import { persistLoginSession } from 'lib/loginSession';
import { useRouter } from 'next/router';
import { getRandomInt } from 'lib/utils/generateRandomInt';
import Image from 'next/image';

const RESEND_COOLDOWN_SECONDS = 60;

declare module 'react' {
  interface HTMLAttributes<T> extends AriaAttributes, DOMAttributes<T> {
    before?: string;
  }
}

/**
 * Convert an absolute or relative URL to a relative URL that preserves
 * the pathname and query string. Used to build the post-verification
 * destination stored alongside the Cognito challenge session.
 */
function toRelativeUrl(absoluteOrRelative: string): string {
  try {
    const url = new URL(absoluteOrRelative);
    return `${url.pathname}${url.search}`;
  } catch {
    return absoluteOrRelative.startsWith('/')
      ? absoluteOrRelative
      : `/${absoluteOrRelative}`;
  }
}

type PasswordlessViewState =
  | { kind: 'form' }
  | { kind: 'sent'; email: string; path: string }
  | { kind: 'error'; email: string; path: string };

type SignupFormTelehealthProps = Pick<
  SignupFormProps,
  | 'redirectOnStateChange'
  | 'readOnlyStateField'
  | 'utmParams'
  | 'isRenewal'
  | 'inputEmail'
  | 'redirectProductEndpoint'
> & {
  src?: string;
  subTitle?: string;
  formSubmitText?: string;
  stateCode?: string;
  className?: string;
  isCardForm?: boolean;
  hasReferral?: boolean;
  isRenewal?: boolean;
  inputEmail?: string;
  totalMedicalCardPrice?: string;
  hasCustomStateCode?: boolean;
  pauseAppointments?: boolean;
  activeTab?: 'new' | 'renew';
  renewFormContentItems?: {
    contentItems: string;
  }[];
  setActiveTab?: (tab: 'new' | 'renew') => void;
  showOptInCheckbox?: boolean;
  showTabs?: boolean;
  passwordless?: boolean;
};

function getCookie(name: string): string | null {
  const match = document.cookie.match(
    new RegExp('(?:^|; )' + name + '=([^;]*)'),
  );
  return match ? decodeURIComponent(match[1]) : null;
}

function FormSubmitMessage() {
  return (
    <div className="mb-1 leading-8 text-center">
      <p>Thank you for choosing Leafwell.</p>
      <p>Someone on our team will be in touch with you soon.</p>
    </div>
  );
}

const SignupFormDefault: React.FC<SignupFormTelehealthProps> = memo(
  ({
    src,
    utmParams,
    stateCode: initialStateCode,
    isCardForm,
    redirectOnStateChange = true,
    isRenewal,
    readOnlyStateField = false,
    inputEmail,
    subTitle,
    formSubmitText,
    hasCustomStateCode = false,
    pauseAppointments = false,
    hasReferral = false,
    activeTab,
    setActiveTab,
    renewFormContentItems,
    showOptInCheckbox = false,
    showTabs = true,
    redirectProductEndpoint,
    passwordless = false,
  }) => {
    const [selectedState, setSelectedState] =
      useState<string>(initialStateCode);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const statesList = useStatesList(isRenewal);
    const { trackEvent } = useEventsTracker();
    const medicalCardParams = useMedicalCardParams({
      initialUrl: src,
      params: utmParams,
    });

    const [isReferral, setReferral] = useState<boolean>(
      medicalCardParams?.email_friend?.length > 0,
    );

    const [formSubmitted, setFormSubmitted] = useState(false);
    const { createSignup, updateSignup } = useMedicalCardSignup();
    const { translations, cardPage, statePages, renewalPages } =
      useWordPressContext();
    const {
      saveUser: saveBrazeUser,
      changeUser,
      requestImmediateDataFlush,
    } = useBrazeContext();

    const [discountCode, setDiscountCode] = useState<string>(null);
    const { query } = useRouter();
    const posthog = usePostHog();
    const sessionId = posthog.get_session_id();
    const distinctId = posthog.get_distinct_id();
    const [fpromTid, setFpromTid] = useState<string | null>(null);
    const [fpromRefId, setFpromRefId] = useState<string | null>(null);

    // ── Passwordless state ──────────────────────────────────────────────
    const [view, setView] = useState<PasswordlessViewState>({ kind: 'form' });
    const [cooldown, setCooldown] = useState<number>(0);
    const [isSending, setIsSending] = useState<boolean>(false);
    const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

    useEffect(() => {
      setFpromTid(getCookie('_fprom_tid'));
      setFpromRefId(getCookie('_fprom_ref'));
    }, []);

    renewFormContentItems = renewFormContentItems || [
      {
        contentItems:
          '<span class="text-dark-green font-medium">Quick</span> and <span class="text-dark-green font-medium">easy</span> process',
      },
      {
        contentItems:
          '<span class="text-dark-green">Money-back</span> guarantee',
      },
      {
        contentItems:
          '<span class="text-dark-green font-medium">Best value</span> in the state',
      },
      {
        contentItems:
          '<span class="text-dark-green font-medium">Access</span> to dispensaries',
      },
    ];

    function setFormErrors(setError, errors) {
      Object.entries(errors).forEach(error => {
        const [name, errorList] = error;
        let errorName = `patient_registry_${name}`;

        if (name === 'organization') {
          errorName = 'stateCode';
        }

        setError(errorName, {
          type: 'server',
          message: errorList[0],
        });
      });
    }

    useEffect(() => {
      if (query.discount_code && query.discount_code != '') {
        setDiscountCode(query.discount_code.toString().toUpperCase());
      }
    }, [query]);

    // Clean up cooldown timer on unmount
    useEffect(() => {
      return () => {
        if (timerRef.current) clearInterval(timerRef.current);
      };
    }, []);

    const handleSelectState = (stateCode?: string) => {
      const hasStateCode = stateCode && stateCode !== '';
      const url = hasStateCode
        ? isRenewal
          ? renewalPages.find(({ code }) => code === stateCode)?.uri
          : statePages.find(({ code }) => code === stateCode)?.uri
        : cardPage?.uri;

      if (hasStateCode) {
        setStateCookie(stateCode);
      }

      if (url && redirectOnStateChange) {
        window.location.assign(`${url}${getTemporaryUtmReferrerQuery()}`);
      }

      // update selected field if a state change - in the form dropdown - is not supposed to trigge
      // the redirect to its medical card page (block content, for example)
      if (!redirectOnStateChange) {
        setSelectedState(stateCode);
      }
    };

    // ── Passwordless helpers ────────────────────────────────────────────

    const startCooldown = useCallback(() => {
      setCooldown(RESEND_COOLDOWN_SECONDS);
      if (timerRef.current) clearInterval(timerRef.current);
      timerRef.current = setInterval(() => {
        setCooldown(prev => {
          if (prev <= 1) {
            clearInterval(timerRef.current!);
            timerRef.current = null;
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }, []);

    const formatTime = (seconds: number) => {
      const m = Math.floor(seconds / 60);
      const s = seconds % 60;
      return `${m}:${s.toString().padStart(2, '0')}`;
    };

    const startSignIn = (email: string) =>
      signIn({
        username: email,
        options: {
          authFlowType: 'USER_AUTH',
          preferredChallenge: 'EMAIL_OTP',
        },
      });

    const initiateSignIn = useCallback(
      async (email: string, path: string) => {
        configureAmplify();
        setIsSending(true);

        try {
          try {
            await signOut();
          } catch {
            // no session to clear
          }

          let result;

          try {
            result = await startSignIn(email);
          } catch (error) {
            if (error?.name !== 'UserNotFoundException') throw error;

            const signUpResult = await signUp({
              username: email,
              options: {
                autoSignIn: { authFlowType: 'USER_AUTH' },
                userAttributes: {
                  given_name: '',
                  family_name: '',
                  phone_number: '',
                  country_code: '',
                  'custom:createdAt': new Date().toISOString(),
                },
              },
            });

            if (signUpResult.nextStep?.signUpStep !== 'COMPLETE_AUTO_SIGN_IN') {
              throw new Error(
                `Unexpected signUp step: ${signUpResult.nextStep?.signUpStep}`,
              );
            }

            trackEvent({
              event: 'user_account_created',
              is_in_app_browser: 'false',
              is_test: 'false',
              page_path: 'medical-card',
              service: 'medicalcard',
            });

            result = await autoSignIn();

            if (
              result.nextStep?.signInStep ===
              'CONTINUE_SIGN_IN_WITH_FIRST_FACTOR_SELECTION'
            ) {
              result = await confirmSignIn({
                challengeResponse: 'EMAIL_OTP',
              });
              console.log(
                '[cognito] after challenge selection:',
                result.nextStep,
              );
            }
          }

          if (
            result.nextStep?.signInStep !== 'CONFIRM_SIGN_IN_WITH_EMAIL_CODE'
          ) {
            console.warn(
              'Unexpected signIn step:',
              result.nextStep?.signInStep,
            );
          }

          await persistLoginSession(email, path);

          setView({ kind: 'sent', email, path });
          startCooldown();
        } catch (error) {
          console.error('Cognito signIn error:', error);
          setView({ kind: 'error', email, path });
        } finally {
          setIsSending(false);
        }
      },
      [startCooldown, trackEvent],
    );

    const handleResend = async () => {
      if (cooldown > 0 || isSending) return;
      if (view.kind !== 'sent') return;
      await initiateSignIn(view.email, view.path);
    };

    const handleRetry = async () => {
      if (view.kind !== 'error') return;
      await initiateSignIn(view.email, view.path);
    };

    const resetToForm = () => setView({ kind: 'form' });

    const onSubmit = async (
      {
        stateCode,
        patient_registry_email,
        patient_registry_email_friend,
        patient_registry_accepted_terms_of_service,
        patient_registry_authorized_share_information,
      },
      { clearErrors, setError },
    ) => {
      if (pauseAppointments) {
        trackEvent({
          event: 'form_submit_medicalcard_down',
          location: isCardForm
            ? hasCustomStateCode
              ? stateCode
              : translations[stateCode.toUpperCase()]
            : 'cannacon',
        });
        setFormSubmitted(true);
      } else if (patient_registry_accepted_terms_of_service) {
        setIsLoading(true);

        // Clear all errors before submit (specially the ones that might've come from the server)
        clearErrors();

        if (!stateCode) {
          setError('stateCode', {
            type: 'client',
            message: 'Invalid state',
          });
          setIsLoading(false);

          return;
        }
        const optIn =
          patient_registry_authorized_share_information?.length > 0 &&
          patient_registry_authorized_share_information[0] === '1'
            ? true
            : false;
        const optInData = showOptInCheckbox
          ? {
              opt_in_to_marketing_emails: optIn,
            }
          : {};
        const params = {
          ...medicalCardParams,
          state_code: stateCode,
          email: patient_registry_email,
          email_friend: patient_registry_email_friend,
          discount_code: discountCode ? discountCode : '',
          ...optInData,
        };
        const createRes = await createSignup(params);

        if (createRes.error) {
          setFormErrors(setError, createRes.errors);
          setIsLoading(false);

          return;
        }

        const updateRes = await updateSignup({
          ...params,
          uuid: createRes.data.uuid,
          accepted_terms_of_service: true,
        });
        const { uuid, redirect_url, error, errors } = updateRes;

        if (error || errors) {
          if (updateRes.data?.redirect_url) {
            window.location.assign(
              `${
                updateRes.data.redirect_url
              }&session_id=${sessionId}&distinct_id=${distinctId}${
                fpromRefId ? `&fp_ref=${fpromRefId}` : ''
              }${fpromTid ? `&fp_tid=${fpromTid}` : ''}`,
            );
          } else {
            setFormErrors(setError, errors);
            setIsLoading(false);
          }

          return;
        }

        // Identify the user with MC uuid - https://js.appboycdn.com/web-sdk/5.1/doc/modules/braze.html#changeuser
        changeUser(createRes.data.uuid);

        // save conversion on Google Ads and Facebook Ads to leafwell.us
        if (
          process.env.NODE_ENV === 'production' &&
          window?.location?.hostname === 'leafwell.us'
        ) {
          // @ts-ignore: function is being initialized on .us pages (index)
          gtag_report_conversion();
          // @ts-ignore: function is being initialized on .us pages (index)
          fbq('track', 'Lead');
        }

        const referrerData = medicalCardParams?.utm_referrer
          ? { page_referrer: medicalCardParams.utm_referrer }
          : {};
        const discountCodeData = discountCode
          ? { discount_code: discountCode }
          : {};

        trackEvent({
          event: 'generate_lead',
          service: 'medical_card',
          location: isCardForm
            ? hasCustomStateCode
              ? stateCode
              : translations[stateCode.toUpperCase()]
            : 'cannacon',
          medical_card_id: uuid,
          email: patient_registry_email,
          email_friend:
            patient_registry_email_friend !== ''
              ? patient_registry_email_friend
              : null,
          client_id: clientId(),
          ...referrerData,
          ...discountCodeData,
          ...optInData,
        });

        // immediately flushes queued data events to braze servers
        // https://js.appboycdn.com/web-sdk/5.1/doc/modules/braze.html#requestimmediatedataflush
        requestImmediateDataFlush();

        saveBrazeUser({
          email: patient_registry_email,
          customAttributes: {
            state: isCardForm
              ? hasCustomStateCode
                ? stateCode
                : translations[stateCode.toUpperCase()]
              : 'cannacon',
          },
        });

        if (fpromTid) {
          posthog.setPersonProperties({ _fprom_tid: fpromTid });
        }
        if (fpromRefId) {
          posthog.setPersonProperties({ _fprom_ref: fpromRefId });
        }

        if (redirect_url) {
          const fullRedirectUrl = `${redirect_url}&session_id=${sessionId}&distinct_id=${distinctId}${
            fpromRefId ? `&fp_ref=${fpromRefId}` : ''
          }${fpromTid ? `&fp_tid=${fpromTid}` : ''}`;

          if (passwordless) {
            const destinationPath = toRelativeUrl(fullRedirectUrl);
            await initiateSignIn(patient_registry_email, destinationPath);
            setIsLoading(false);
          } else {
            setIsLoading(false);
            window.location.assign(fullRedirectUrl);
          }
        }
      }
    };

    const selectedStateObject: StatesListItem = useMemo(
      () => statesList.find(item => item.value === initialStateCode),
      [statesList, initialStateCode],
    );

    const renderSentView = () => (
      <div className="grid gap-y-4">
        <p className="font-medium">Check your email</p>
        <p className="text-sm">
          We sent a sign-in link to{' '}
          <strong className="break-all">
            {view.kind === 'sent' && view.email}
          </strong>
          .<br /> Open it to continue your application.
        </p>
        <div className="flex items-center gap-2">
          <button
            type="button"
            onClick={handleResend}
            disabled={cooldown > 0 || isSending}
            className={[
              'text-base font-medium underline',
              cooldown === 0 && !isSending
                ? 'text-yellow-600'
                : 'cursor-not-allowed text-yellow-600',
            ].join(' ')}
          >
            {isSending ? 'Sending…' : 'Send it again'}
          </button>
          {cooldown > 0 && (
            <span className="text-sm tabular-nums text-yellow-700">
              {formatTime(cooldown)}
            </span>
          )}
        </div>
        <div className="grid gap-y-1 text-sm text-gray-700">
          <p>Can&apos;t find it? Check your spam folder.</p>
          <p>
            Wrong email?{' '}
            <button
              type="button"
              onClick={resetToForm}
              className="font-medium underline"
            >
              Start over
            </button>
          </p>
        </div>
      </div>
    );

    const renderErrorView = () => (
      <div className="grid gap-y-4">
        <p className="font-medium">We couldn&apos;t send your link</p>
        <p className="text-sm">
          The sign-in link to{' '}
          <strong className="break-all">
            {view.kind === 'error' && view.email}
          </strong>{' '}
          didn&apos;t go through. Try again, or start over with a different
          email.
        </p>
        <div className="grid gap-y-2">
          <Button
            label="Try again"
            variant="secondary"
            className="w-full rounded-lg"
            loading={isSending}
            onClick={handleRetry}
          />
          <button
            type="button"
            onClick={resetToForm}
            className="text-sm font-medium underline"
          >
            Start over
          </button>
        </div>
      </div>
    );

    return (
      <div className="rounded-xl md:p-4 min-w-20 max-w-xl">
        {/* Tabs */}
        {showTabs ? (
          <div className="flex gap-x-2 xs:gap-x-4">
            <button
              onClick={() => setActiveTab('new')}
              className={`w-1/2 py-3 text-xs xs:text-sm font-semibold form-tab ${
                activeTab === 'new' ? 'form-tab-selected' : ''
              }`}
            >
              New Leafwell patient
            </button>
            <button
              onClick={() => setActiveTab('renew')}
              className={`w-1/2 py-3 text-xs xs:text-sm font-semibold form-tab ${
                activeTab === 'renew' ? 'form-tab-selected' : ''
              }`}
            >
              Returning patient
            </button>
          </div>
        ) : null}
        {activeTab === 'new' ? (
          <div
            className={`bg-white p-6 form-body ${
              showTabs ? '' : 'border-t border-t-[#C3CDD5] !rounded-t-lg'
            }`}
          >
            {passwordless && view.kind === 'error' ? (
              renderErrorView()
            ) : passwordless && view.kind === 'sent' ? (
              renderSentView()
            ) : (
              <Form
                className="md:gap-x-3 gap-y-4"
                mode="onSubmit"
                defaultValues={{
                  stateCode: selectedState,
                  patient_registry_email: inputEmail
                    ? inputEmail
                    : medicalCardParams?.email ?? '',
                  patient_registry_email_friend:
                    medicalCardParams?.email_friend ?? '',
                }}
                onSubmit={onSubmit}
              >
                {hasReferral ? (
                  <div className="flex justify-between bg-white border rounded-lg border-[#C3CDD5] w-full mb-2 p-0.5">
                    <div
                      className={[
                        'w-1/2 flex justify-center items-center py-2 font-semibold text-sm cursor-pointer',
                        isReferral ? 'bg-white' : 'bg-green-50  rounded-lg',
                      ].join(' ')}
                      onClick={() => setReferral(false)}
                    >
                      Just for you
                    </div>
                    <div
                      className={[
                        'relative w-1/2 flex justify-center items-center py-2 border rounded-lg font-semibold text-sm cursor-pointer',
                        isReferral
                          ? 'bg-green-50 rounded-lg'
                          : 'bg-white border-white',
                      ].join(' ')}
                      onClick={() => setReferral(true)}
                    >
                      For you + a friend
                      <span className="absolute -top-4 -right-4 py-0.5 px-1 rounded text-sm text-white font-semibold bg-[#039855]">
                        10% OFF
                      </span>
                    </div>
                  </div>
                ) : null}
                {subTitle && (
                  <p
                    className="text-lg lg:text-2xl  font-semibold text-center mb-2"
                    dangerouslySetInnerHTML={{ __html: subTitle }}
                  />
                )}
                {discountCode ? (
                  <div className="bg-green-50 border border-green-200 rounded-xl text-center text-xs p-2">
                    <strong>{discountCode}</strong> Discount Applied!
                  </div>
                ) : null}
                {isCardForm && !hasCustomStateCode && (
                  <div className="grid gap-y-2 js-form-field relative">
                    <p className="font-medium">{`You're applying for`}</p>
                    <FieldWrap name="stateCode">
                      <SelectFieldMinimal
                        aria-label="Select your state"
                        defaultValue={selectedStateObject}
                        placeholder={translations['Select State']}
                        options={statesList}
                        disabled={readOnlyStateField}
                        onSelect={target => handleSelectState(target.value)}
                      />
                    </FieldWrap>
                  </div>
                )}
                {formSubmitted ? (
                  <FormSubmitMessage />
                ) : (
                  <>
                    <div className="grid gap-y-2 js-form-field relative">
                      <p className="font-medium">
                        Email <sup>*</sup>
                      </p>
                      <Input
                        className="rounded-lg"
                        name="patient_registry_email"
                        placeholder="Email *"
                        type="email"
                        validations={['required', 'email']}
                      />
                    </div>
                    {isReferral ? (
                      <div className="grid gap-y-2 js-form-field relative">
                        <p className="font-medium">
                          Friend's Email <sup>*</sup>
                        </p>
                        <Input
                          className="rounded-lg"
                          name="patient_registry_email_friend"
                          placeholder="Friend's Email *"
                          type="email"
                          validations={['required', 'email']}
                        />
                      </div>
                    ) : null}
                    <div className="grid gap-y-2 js-form-field relative">
                      <Toggle
                        className="border-gray-300"
                        type="checkbox"
                        name="patient_registry_accepted_terms_of_service"
                        validations={['required']}
                        options={[
                          {
                            label: (
                              <p className="text-sm">
                                {translations['I have read and accept the']}{' '}
                                <a
                                  target="_blank"
                                  href="https://leafwell.com/terms-of-use"
                                >
                                  {translations['terms of service']}
                                </a>{' '}
                                {translations['and']}{' '}
                                <a
                                  target="_blank"
                                  href="https://leafwell.com/privacy-policy"
                                >
                                  {translations['privacy policy']}
                                </a>
                              </p>
                            ),
                            value: '1',
                          },
                        ]}
                      />
                    </div>
                    {showOptInCheckbox && (
                      <div className="grid gap-y-2 js-form-field relative">
                        <Toggle
                          className="border-gray-300"
                          type="checkbox"
                          name="patient_registry_authorized_share_information"
                          options={[
                            {
                              label: (
                                <p className="text-sm">
                                  I authorize Leafwell to{' '}
                                  <a
                                    target="_blank"
                                    href="https://leafwell.com//illinois-partner-user-agreement"
                                  >
                                    share my personal information
                                  </a>{' '}
                                  with licensed Illinois dispensaries
                                </p>
                              ),
                              value: '1',
                            },
                          ]}
                        />
                      </div>
                    )}
                    <div className="c-form__actions mt-2 w-full md:w-fit md:justify-self-end">
                      <Button
                        type="submit"
                        label={formSubmitText}
                        variant="secondary"
                        name="commit"
                        id="submitt_embeded_step_1"
                        className="w-full md:w-fit md:px-20 rounded-lg"
                        loading={isLoading}
                        data-disable-with={formSubmitText}
                      />
                    </div>
                  </>
                )}
              </Form>
            )}
          </div>
        ) : (
          <div className="bg-white p-6 form-body grid gap-y-3">
            {renewFormContentItems?.length > 0 && (
              <ul className="grid gap-y-6 xl:min-w-100">
                {renewFormContentItems?.map(item => {
                  const idx = getRandomInt(1, 1000000);
                  return (
                    <li
                      key={`${idx}-renew-form-content-item`}
                      className="flex gap-x-2 items-center"
                    >
                      <Image
                        src="/check-green-transparent.svg"
                        alt="check"
                        width={20}
                        height={20}
                      />
                      <Paragraph
                        dangerouslySetInnerHTML={{ __html: item?.contentItems }}
                      />
                    </li>
                  );
                })}
              </ul>
            )}
            <Button
              href={process.env.NEXT_PUBLIC_LOGIN_URL}
              label={translations['Login']}
              variant="secondary"
              className="w-full md:w-fit md:px-20 rounded-lg mt-4 justify-self-end"
              data-disable-with={translations['Login']}
            />
          </div>
        )}
      </div>
    );
  },
);

const SignupFormTelehealth: React.FC<SignupFormTelehealthProps> = props => {
  return <SignupFormDefault {...{ ...props, isCardForm: props?.isCardForm }} />;
};

export default SignupFormTelehealth;
